/**
 * Every cache key in one tree. Invalidation reads as
 * `queryClient.invalidateQueries({ queryKey: queryKeys.auth.all })`.
 */
export const queryKeys = {
  compliance: {
    all: ['compliance'] as const,
    catalog: (locationId: string, actorId: string, role: string) =>
      ['compliance', 'catalog', locationId, actorId, role] as const,
    appointments: (locationId: string) => ['compliance', 'appointments', locationId] as const,
    settings: (locationId: string) => ['compliance', 'settings', locationId] as const,
    record: (locationId: string, customerId: string, actorId: string) =>
      [...queryKeys.compliance.all, 'record', locationId, customerId, actorId] as const,
    exclusions: (locationId: string, customerId: string, actorId: string) =>
      [...queryKeys.compliance.all, 'exclusions', locationId, customerId, actorId] as const,
    customers: (locationId: string) =>
      [...queryKeys.compliance.all, 'customers', locationId] as const,
    customer: (locationId: string, customerId: string) =>
      [...queryKeys.compliance.all, 'customer', locationId, customerId] as const,
  },
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },
  invitations: {
    all: ['invitations'] as const,
    roles: () => [...queryKeys.invitations.all, 'roles'] as const,
    preview: (token: string) => [...queryKeys.invitations.all, 'preview', token] as const,
  },
  customers: {
    all: ['customers'] as const,
    // Structural, not `CustomerQuery`: `lib/` must not import from features.
    list: (query: Record<string, unknown> = {}) =>
      [...queryKeys.customers.all, 'list', query] as const,
    detail: (customerId: string) => [...queryKeys.customers.all, 'detail', customerId] as const,
  },
  scheduler: {
    all: ['scheduler'] as const,
    calendar: (from: string, to: string) =>
      [...queryKeys.scheduler.all, 'calendar', from, to] as const,
    contacts: () => [...queryKeys.scheduler.all, 'contacts'] as const,
  },
  billing: {
    all: ['billing'] as const,
    packages: () => [...queryKeys.billing.all, 'packages'] as const,
    purchases: (customerId: string) => [...queryKeys.billing.all, 'purchases', customerId] as const,
  },
  platform: {
    all: ['platform'] as const,
    ablefyProducts: () => [...queryKeys.platform.all, 'ablefy-products'] as const,
    readyForSale: () => [...queryKeys.platform.all, 'ready-for-sale'] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: () => [...queryKeys.notifications.all, 'list'] as const,
  },
  settings: {
    all: ['settings'] as const,
    workspace: () => [...queryKeys.settings.all, 'workspace'] as const,
  },
  workspace: {
    all: ['workspace'] as const,
    settings: () => [...queryKeys.workspace.all, 'settings'] as const,
    stageLabels: () => [...queryKeys.workspace.all, 'stage-labels'] as const,
  },
  pipeline: {
    all: ['pipeline'] as const,
    column: (stage: string) => [...queryKeys.pipeline.all, 'column', stage] as const,
  },
  calls: {
    all: ['calls'] as const,
    list: () => [...queryKeys.calls.all, 'list'] as const,
  },
} as const
